import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilm, faTv, faStar, faBookmark as solidBookmark } from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar, faBookmark as regularBookmark } from '@fortawesome/free-regular-svg-icons';
import MediaModal from '../components/MediaModal';
import { fetchMovieDetails, fetchContentDetails } from '../services/tmdb';

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    yourWatchlist: 'YOUR WATCHLIST',
    movies: 'Movies',
    series: 'Series',
    noMoviesInWatchlist: 'No movies in your watchlist',
    noSeriesInWatchlist: 'No series in your watchlist',
  },
  fr: {
    yourWatchlist: 'VOTRE LISTE DE SUIVI',
    movies: 'Films',
    series: 'Séries',
    noMoviesInWatchlist: 'Aucun film dans votre liste de suivi',
    noSeriesInWatchlist: 'Aucune série dans votre liste de suivi',
  },
  es: {
    yourWatchlist: 'TU LISTA DE SEGUIMIENTO',
    movies: 'Películas',
    series: 'Series',
    noMoviesInWatchlist: 'No hay películas en tu lista de seguimiento',
    noSeriesInWatchlist: 'No hay series en tu lista de seguimiento',
  },
  it: {
    yourWatchlist: 'LA TUA LISTA',
    movies: 'Film',
    series: 'Serie TV',
    noMoviesInWatchlist: 'Nessun film nella tua lista',
    noSeriesInWatchlist: 'Nessuna serie nella tua lista',
  },
  nl: {
    yourWatchlist: 'JOUW WATCHLIST',
    movies: 'Films',
    series: 'Series',
    noMoviesInWatchlist: 'Geen films in je watchlist',
    noSeriesInWatchlist: 'Geen series in je watchlist',
  },
};

const ContentFilter = ({ activeFilter, setActiveFilter, lang }) => {
  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  return (
    <div className="content-filter">
      <button 
        className={`filter-btn ${activeFilter === 'movies' ? 'active' : ''}`}
        onClick={() => setActiveFilter('movies')}
      >
        <FontAwesomeIcon icon={faFilm} />
        {t('movies')}
      </button>
      <div className="filter-divider"></div>
      <button 
        className={`filter-btn ${activeFilter === 'series' ? 'active' : ''}`}
        onClick={() => setActiveFilter('series')}
      >
        <FontAwesomeIcon icon={faTv} />
        {t('series')}
      </button>
    </div>
  );
};

const MediaCard = ({ item, onCardClick, isFavorite, refreshWatchlist }) => {
  const [isPending, setIsPending] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const timerRef = useRef(null);

  const createStarRating = (rating) => {
    const starRating = rating / 2;
    const fullStars = Math.floor(starRating);
    const partialStar = starRating - fullStars;
    
    let starsHTML = [];
    
    for (let i = 0; i < fullStars; i++) {
      starsHTML.push(
        <div className="star" key={`full-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '100%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    if (partialStar > 0) {
      starsHTML.push(
        <div className="star" key="partial">
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    const emptyStars = 5 - Math.ceil(starRating);
    for (let i = 0; i < emptyStars; i++) {
      starsHTML.push(
        <div className="star" key={`empty-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '0%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    return starsHTML;
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    const key = `${item.type}-${item.id}`;

    if (isPending && pendingId === key) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setIsPending(false);
      setPendingId(null);
      return;
    }

    setIsPending(true);
    setPendingId(key);

    timerRef.current = setTimeout(() => {
      const currentWatchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
      const stillExists = currentWatchlist.some(w => w.id === item.id && w.type === item.type);
      
      if (stillExists) {
        const updatedWatchlist = currentWatchlist.filter(w => !(w.id === item.id && w.type === item.type));
        localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
        refreshWatchlist();
      }
      
      setIsPending(false);
      setPendingId(null);
      timerRef.current = null;
    }, 2500);
  };

  const posterPath = item.poster_path 
    ? `https://image.tmdb.org/t/p/w500${item.poster_path}`
    : 'https://via.placeholder.com/500x750?text=No+Poster';

  const title = item.title || item.name || 'UNTITLED';
  
  const truncateTitle = (title, maxLength = 12) => {
    if (title.length > maxLength) {
      return title.substring(0, maxLength) + '...';
    }
    return title;
  };

  const isPendingThis = isPending && pendingId === `${item.type}-${item.id}`;

  const circleRadius = 14;
  const strokeWidth = 2.5;
  const circumference = 2 * Math.PI * circleRadius;

  return (
    <div 
      className="movie-card"
      onClick={() => onCardClick(item)}
    >
      <div className="poster-container">
        <img 
          src={posterPath} 
          alt={title} 
          className="movie-poster" 
          loading="lazy" 
        />
        <div className="favorites-bookmark-wrapper">
          <button 
            className={`favorites-bookmark-btn ${isFavorite ? 'active' : ''} ${isPendingThis ? 'pending' : ''}`}
            onClick={handleToggle}
            aria-label="Toggle watchlist"
          >
            <FontAwesomeIcon icon={isPendingThis ? regularBookmark : (isFavorite ? solidBookmark : regularBookmark)} />
          </button>
          {isPendingThis && (
            <svg
              className="favorites-pending-spinner"
              viewBox="0 0 32 32"
              width="32"
              height="32"
              style={{
                position: 'absolute',
                top: '-2px',
                left: '-2px',
                transform: 'rotate(-90deg)',
                width: 'calc(100% + 4px)',
                height: 'calc(100% + 4px)',
              }}
            >
              <circle
                cx="16"
                cy="16"
                r={circleRadius}
                fill="none"
                stroke="#ffffff"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={0}
                strokeLinecap="round"
                style={{
                  animation: 'spinnerDash 2.5s linear forwards',
                }}
              />
            </svg>
          )}
        </div>
      </div>
      <div className="movie-details">
        <div className="movie-title" title={title}>
          {truncateTitle(title).toUpperCase()}
        </div>
        <div className="star-rating">
          {createStarRating(item.vote_average || 0)}
        </div>
      </div>
    </div>
  );
};

const FavoritesPage = () => {
  const [activeFilter, setActiveFilter] = useState('movies');
  const [watchlist, setWatchlist] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const [searchParams, setSearchParams] = useSearchParams();

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  const t = (key) => {
    const langData = translations[language] || translations.en;
    return langData[key] || key;
  };

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLanguage(e.newValue || 'en');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const storedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
    setWatchlist(storedWatchlist);
  }, []);

  useEffect(() => {
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');
    
    if (movieId && !isNaN(parseInt(movieId))) {
      openContentModal(parseInt(movieId), 'movie');
    } else if (seriesId && !isNaN(parseInt(seriesId))) {
      openContentModal(parseInt(seriesId), 'tv');
    }
  }, []);

  useEffect(() => {
    const filtered = watchlist.filter(item => 
      activeFilter === 'movies' ? item.type === 'movie' : item.type === 'tv'
    );
    setFilteredItems(filtered);
  }, [watchlist, activeFilter]);

  const openContentModal = async (contentId, type) => {
    setIsLoading(true);
    try {
      let details;
      if (type === 'movie') {
        details = await fetchMovieDetails(contentId);
      } else if (type === 'tv') {
        details = await fetchContentDetails('tv', contentId);
      }

      if (details) {
        setSelectedMedia({ ...details, media_type: type });
        setSelectedMediaType(type);
        setIsModalOpen(true);
        
        const newSearchParams = new URLSearchParams(searchParams);
        if (type === 'movie') {
          newSearchParams.set('movie', contentId);
          newSearchParams.delete('series');
        } else {
          newSearchParams.set('series', contentId);
          newSearchParams.delete('movie');
        }
        setSearchParams(newSearchParams);
      }
    } catch (error) {
      console.error('Error fetching content details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = async (item) => {
    openContentModal(item.id, item.type);
  };

  const handleMediaChange = (media, mediaType) => {
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    const params = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      params.set('movie', media.id);
      params.delete('series');
    } else {
      params.set('series', media.id);
      params.delete('movie');
    }
    setSearchParams(params);
  };

  const refreshWatchlist = () => {
    const updatedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
    setWatchlist(updatedWatchlist);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
    setSelectedMediaType(null);
    
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('movie');
    newSearchParams.delete('series');
    setSearchParams(newSearchParams);
  };

  const reversedItems = [...filteredItems].reverse();

  const pcBackground = 'https://image.tmdb.org/t/p/w780/ys7kESqqKpM4XO9EGM7ikAbNvVh.jpg';
  const mobileBackground = 'https://image.tmdb.org/t/p/w780/ys7kESqqKpM4XO9EGM7ikAbNvVh.jpg';

  return (
    <div id="favoritesPage" className="page-content">
      <div 
        className="favorites-page-bg"
        style={{ 
          backgroundImage: `url(${isMobile ? mobileBackground : pcBackground})`
        }}
      ></div>
      <div className="favorites-overlay"></div>
      
      <h1 className="page-title">{t('yourWatchlist')}</h1>
      
      <ContentFilter 
        activeFilter={activeFilter} 
        setActiveFilter={setActiveFilter} 
        lang={language}
      />
      
      {reversedItems.length === 0 ? (
        <div className="empty-page">
          <p>
            {activeFilter === 'movies' 
              ? t('noMoviesInWatchlist')
              : t('noSeriesInWatchlist')}
          </p>
        </div>
      ) : (
        <div className="favorites-grid">
          {reversedItems.map(item => (
            <MediaCard 
              key={`${item.type}-${item.id}`} 
              item={item}
              onCardClick={handleCardClick}
              isFavorite={true}
              refreshWatchlist={refreshWatchlist}
            />
          ))}
        </div>
      )}

      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {isModalOpen && selectedMedia && selectedMediaType && (
        <MediaModal
          media={selectedMedia}
          mediaType={selectedMediaType}
          onClose={closeModal}
          onMediaChange={handleMediaChange}
          onUpdate={refreshWatchlist}
        />
      )}
      
      <style jsx>{`
        .favorites-page-bg {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-size: cover;
          background-position: center;
          z-index: -2;
        }
        .favorites-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgb(8, 8, 8);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: -1;
        }
        .page-title {
          text-align: center;
          font-size: 2rem;
          font-weight: 700;
          color: white;
          margin-top: 30px;
          margin-bottom: 20px;
          letter-spacing: 2px;
          text-shadow: 0 2px 10px rgba(0,0,0,0.5);
        }
        .content-filter {
          display: flex;
          justify-content: center;
          background: rgba(30,30,30,0.8);
          border-radius: 30px;
          padding: 0;
          margin: 20px auto 30px;
          width: 100%;
          max-width: 300px;
          height: 50px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .filter-btn {
          flex: 1;
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.6);
          
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
        }
        .filter-btn.active {
          color: white;
          background: rgba(255,255,255,0.15);
        }
        .filter-divider {
          width: 1px;
          background: rgba(255,255,255,0.1);
          height: 100%;
        }
        .favorites-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 25px;
          padding: 0 15px;
          max-width: 1400px;
          margin: 0 auto;
        }
        .movie-card {
          position: relative;
          border-radius: 8px;
          overflow: visible;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: pointer;
          background: transparent;
        }
        .poster-container {
          position: relative;
          width: 100%;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.5);
          background: #1a1a1a;
        }
        .movie-poster {
          width: 100%;
          display: block;
          transition: transform 0.5s ease;
        }
        .movie-card:hover .movie-poster {
          transform: scale(1.05);
        }

        /* ===== UNIQUE BOOKMARK CLASSES (fixed collision) ===== */
        .favorites-bookmark-wrapper {
          position: absolute;
          top: 8px !important;
          right: 8px !important;
          width: 32px !important;
          height: 32px !important;
          min-width: 32px !important;
          min-height: 32px !important;
          z-index: 5;
          overflow: visible;
          flex-shrink: 0;
          box-sizing: border-box;
        }

        .favorites-bookmark-btn {
          position: absolute;
          top: 0 !important;
          left: 0 !important;
          right: auto !important;
          width: 100% !important;
          height: 100% !important;
          min-width: 32px !important;
          min-height: 32px !important;
          max-width: 32px !important;
          max-height: 32px !important;
          background: rgba(0,0,0,0.5);
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s ease, background 0.2s, transform 0.3s ease;
          backdrop-filter: blur(4px);
          padding: 0;
          margin: 0;
          z-index: 2;
          box-sizing: border-box;
        }

        .favorites-bookmark-btn.active {
          color: white;
        }

        .favorites-bookmark-btn.pending {
          transform: scale(0.85);
          color: rgba(255,255,255,0.5);
        }

        .movie-card:hover .favorites-bookmark-btn {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .favorites-bookmark-wrapper {
            width: 28px !important;
            height: 28px !important;
            min-width: 28px !important;
            min-height: 28px !important;
            top: 6px !important;
            right: 6px !important;
          }
          .favorites-bookmark-btn {
            min-width: 28px !important;
            min-height: 28px !important;
            max-width: 28px !important;
            max-height: 28px !important;
            font-size: 14px;
            opacity: 1 !important;
          }
        }

        /* ===== PENDING SPINNER (unique class) ===== */
        .favorites-pending-spinner {
          position: absolute;
          top: -2px;
          left: -2px;
          width: calc(100% + 4px);
          height: calc(100% + 4px);
          pointer-events: none;
          z-index: 1;
        }

        @keyframes spinnerDash {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: ${2 * Math.PI * 14};
          }
        }

        .movie-details {
          padding: 8px 0px !important;
          text-align: left !important;
        }
        .movie-title {
          font-weight: 600;
          font-size: clamp(12px, 1.1vw, 16px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #fff;
          letter-spacing: 0.3px;
          padding: 0 2px;
        }
        .star-rating {
          display: flex;
          align-items: center;
          gap: 2px;
          margin: 0;
          padding: 0;
        }
        .star {
          position: relative;
          display: inline-block;
          font-size: clamp(10px, 0.8vw, 14px);
          color: #555;
          line-height: 1;
          margin: 0;
          padding: 0;
        }
        .star .star-filled {
          position: absolute;
          top: 0;
          left: 0;
          overflow: hidden;
          color: #59f001;
        }
        .star .far.fa-star {
          color: #444;
        }
        .empty-page {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 300px;
          color: rgba(255,255,255,0.7);
          font-size: 1.2rem;
          padding: 20px;
        }
        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }
        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(255,255,255,0.2);
          border-top-color: #59f001;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1600px) {
          .favorites-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 20px;
          }
        }
        @media (max-width: 1400px) {
          .favorites-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        @media (max-width: 1200px) {
          .favorites-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (max-width: 992px) {
          .favorites-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            padding: 0 10px;
          }
        }
        @media (max-width: 768px) {
          .favorites-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .page-title {
            font-size: 1.5rem;
            margin-top: 20px;
          }
          .content-filter {
            max-width: 240px;
            height: 44px;
          }
          .filter-btn {
            font-size: 14px;
          }
        }
        @media (max-width: 480px) {
          .movie-title {
            font-size: 10px;
          }
          .star {
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default FavoritesPage;